import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { MapPinIcon, DownloadIcon, UsersIcon, ClockIcon, BellIcon, SmartphoneIcon, LockIcon, ShareIcon, WifiOffIcon, CameraIcon, AlertTriangleIcon, CloudIcon, ShieldIcon } from 'lucide-react';
interface PlatformFeature {
  icon: React.ReactNode;
  text: string;
}
interface PlatformRow {
  title: string;
  description: string;
  features: PlatformFeature[];
  mockup: 'web' | 'mobile' | 'dashcam';
  reverse?: boolean;
}
const platformRows: PlatformRow[] = [{
  title: 'Panel de Control Web',
  description: 'Gestiona toda tu flotilla desde cualquier navegador con nuestra plataforma profesional.',
  features: [{
    icon: <MapPinIcon className="w-4 h-4" data-id="element-278" />,
    text: 'Vista de flotilla completa en mapa'
  }, {
    icon: <DownloadIcon className="w-4 h-4" data-id="element-279" />,
    text: 'Reportes descargables PDF/Excel'
  }, {
    icon: <UsersIcon className="w-4 h-4" data-id="element-280" />,
    text: 'Múltiples usuarios con permisos'
  }, {
    icon: <ClockIcon className="w-4 h-4" data-id="element-281" />,
    text: 'Historial de rutas 90 días'
  }, {
    icon: <BellIcon className="w-4 h-4" data-id="element-282" />,
    text: 'Alertas configurables'
  }],
  mockup: 'web'
}, {
  title: 'App Móvil Intuitiva',
  description: 'Controla tus vehículos desde tu smartphone con nuestra app nativa.',
  features: [{
    icon: <SmartphoneIcon className="w-4 h-4" data-id="element-283" />,
    text: 'Disponible iOS y Android'
  }, {
    icon: <BellIcon className="w-4 h-4" data-id="element-284" />,
    text: 'Notificaciones push instantáneas'
  }, {
    icon: <LockIcon className="w-4 h-4" data-id="element-285" />,
    text: 'Bloqueo de motor con 1 tap'
  }, {
    icon: <ShareIcon className="w-4 h-4" data-id="element-286" />,
    text: 'Compartir ubicación familiar'
  }, {
    icon: <WifiOffIcon className="w-4 h-4" data-id="element-287" />,
    text: 'Modo offline disponible'
  }],
  mockup: 'mobile',
  reverse: true
}, {
  title: 'Dashcam & Visión Inteligente',
  description: 'Tecnología de video avanzada para máxima seguridad y evidencia.',
  features: [{
    icon: <CameraIcon className="w-4 h-4" data-id="element-288" />,
    text: 'Video en vivo 24/7'
  }, {
    icon: <AlertTriangleIcon className="w-4 h-4" data-id="element-289" />,
    text: 'Detección de fatiga DMS'
  }, {
    icon: <ShieldIcon className="w-4 h-4" data-id="element-290" />,
    text: 'Alertas ADAS en tiempo real'
  }, {
    icon: <CloudIcon className="w-4 h-4" data-id="element-291" />,
    text: '1GB almacenamiento nube incluido'
  }, {
    icon: <DownloadIcon className="w-4 h-4" data-id="element-292" />,
    text: 'Evidencia ante siniestros'
  }],
  mockup: 'dashcam'
}];
export function PlatformSection() {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  return <section id="plataforma" ref={ref} className="w-full bg-brand-bg py-20 md:py-28" data-id="element-293">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-294">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16 md:mb-20" data-id="element-295">
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4" data-id="element-296">
            Una plataforma. Toda la visibilidad.
          </h2>
          <p className="font-body text-lg text-brand-text-secondary max-w-2xl mx-auto" data-id="element-297">
            Accede a tus vehículos desde cualquier dispositivo, en cualquier
            momento.
          </p>
        </motion.div>

        {/* Platform Rows */}
        <div className="space-y-20 md:space-y-28" data-id="element-298">
          {platformRows.map((row, index) => <PlatformRow key={row.title} row={row} index={index} isVisible={isVisible} data-id="element-299" />)}
        </div>
      </div>
    </section>;
}
function PlatformRow({
  row,
  index,
  isVisible
}: {
  row: PlatformRow;
  index: number;
  isVisible: boolean;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 40
  }} animate={isVisible ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.7,
    delay: index * 0.2
  }} className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${row.reverse ? 'lg:flex-row-reverse' : ''}`} data-id="element-300">
      {/* Content */}
      <div className={row.reverse ? 'lg:order-2' : ''} data-id="element-301">
        <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4" data-id="element-302">
          {row.title}
        </h3>
        <p className="font-body text-brand-text-secondary mb-6" data-id="element-303">
          {row.description}
        </p>
        <ul className="space-y-3" data-id="element-304">
          {row.features.map(feature => <li key={feature.text} className="flex items-center gap-3" data-id="element-305">
              <div className="w-8 h-8 rounded-lg bg-brand-accent/10 flex items-center justify-center text-brand-accent flex-shrink-0" data-id="element-306">
                {feature.icon}
              </div>
              <span className="font-body text-brand-text-secondary" data-id="element-307">
                {feature.text}
              </span>
            </li>)}
        </ul>
      </div>

      {/* Mockup */}
      <div className={row.reverse ? 'lg:order-1' : ''} data-id="element-308">
        {row.mockup === 'web' && <WebPlatformMockup data-id="element-309" />}
        {row.mockup === 'mobile' && <MobileAppMockup data-id="element-310" />}
        {row.mockup === 'dashcam' && <DashcamMockup data-id="element-311" />}
      </div>
    </motion.div>;
}
function WebPlatformMockup() {
  return <div className="glass rounded-2xl p-4 shadow-2xl shadow-brand-accent/10" data-id="element-312">
      {/* Browser Chrome */}
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-cyan-subtle" data-id="element-313">
        <div className="flex gap-1.5" data-id="element-314">
          <div className="w-3 h-3 rounded-full bg-red-500/60" data-id="element-315" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/60" data-id="element-316" />
          <div className="w-3 h-3 rounded-full bg-green-500/60" data-id="element-317" />
        </div>
        <div className="flex-1 bg-brand-bg rounded-lg px-3 py-1.5 ml-4" data-id="element-318">
          <span className="font-mono text-xs text-brand-text-secondary" data-id="element-319">
            app.trackerspacemx.com
          </span>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="grid grid-cols-4 gap-3" data-id="element-320">
        {/* Sidebar */}
        <div className="col-span-1 bg-brand-bg rounded-lg p-3 space-y-3" data-id="element-321">
          <div className="h-8 bg-brand-surface rounded flex items-center px-2" data-id="element-322">
            <span className="font-subheading text-xs text-white" data-id="element-323">
              Dashboard
            </span>
          </div>
          <div className="h-6 bg-brand-surface/50 rounded" data-id="element-324" />
          <div className="h-6 bg-brand-surface/50 rounded" data-id="element-325" />
          <div className="h-6 bg-brand-surface/50 rounded" data-id="element-326" />
          <div className="h-6 bg-brand-surface/50 rounded" data-id="element-327" />
        </div>

        {/* Main Content */}
        <div className="col-span-3 space-y-3" data-id="element-328">
          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-2" data-id="element-329">
            {['12', '8', '4', '2'].map((val, i) => <div key={i} className="bg-brand-bg rounded-lg p-2 text-center" data-id="element-330">
                <p className="font-heading text-lg text-white" data-id="element-331">{val}</p>
                <p className="font-body text-[10px] text-brand-text-secondary" data-id="element-332">
                  {['Total', 'Activos', 'Parados', 'Alertas'][i]}
                </p>
              </div>)}
          </div>

          {/* Map Area */}
          <div className="bg-brand-bg rounded-lg p-3 min-h-[180px] relative overflow-hidden" data-id="element-333">
            <div className="absolute inset-0 bg-grid opacity-20" data-id="element-334" />
            {/* Vehicle Markers */}
            {[{
            top: '25%',
            left: '30%'
          }, {
            top: '45%',
            left: '55%'
          }, {
            top: '60%',
            left: '35%'
          }, {
            top: '35%',
            left: '70%'
          }].map((pos, i) => <div key={i} className="absolute" style={{
            top: pos.top,
            left: pos.left
          }} data-id="element-335">
                <div className="w-3 h-3 rounded-full bg-brand-accent" data-id="element-336">
                  <div className="w-1.5 h-1.5 rounded-full bg-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-337" />
                </div>
              </div>)}
            {/* Route */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-338">
              <path d="M80,45 Q120,80 160,60 T240,90" fill="none" stroke="#00c8ff" strokeWidth="1.5" strokeOpacity="0.5" data-id="element-339" />
            </svg>
          </div>
        </div>
      </div>
    </div>;
}
function MobileAppMockup() {
  return <div className="flex justify-center" data-id="element-340">
      <div className="w-[240px] bg-brand-bg rounded-[2.5rem] p-3 border-4 border-brand-surface shadow-2xl shadow-brand-accent/10" data-id="element-341">
        <div className="bg-brand-surface rounded-[2rem] overflow-hidden" data-id="element-342">
          {/* Status Bar */}
          <div className="flex items-center justify-between px-5 py-2.5 bg-brand-primary" data-id="element-343">
            <span className="font-mono text-xs text-white" data-id="element-344">9:41</span>
            <div className="flex items-center gap-1" data-id="element-345">
              <div className="w-4 h-2.5 bg-white rounded-sm" data-id="element-346" />
            </div>
          </div>

          {/* App Header */}
          <div className="px-4 py-3 bg-brand-primary border-b border-cyan-subtle flex items-center justify-between" data-id="element-347">
            <span className="font-subheading font-medium text-white" data-id="element-348">
              Mis Vehículos
            </span>
            <BellIcon className="w-5 h-5 text-brand-accent" data-id="element-349" />
          </div>

          {/* Map */}
          <div className="h-40 bg-brand-bg relative" data-id="element-350">
            <div className="absolute inset-0 bg-grid opacity-30" data-id="element-351" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-352">
              <div className="w-5 h-5 rounded-full bg-brand-accent animate-ping absolute" data-id="element-353" />
              <div className="w-5 h-5 rounded-full bg-brand-accent relative flex items-center justify-center" data-id="element-354">
                <div className="w-2.5 h-2.5 rounded-full bg-white" data-id="element-355" />
              </div>
            </div>
            {/* Route */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-356">
              <path d="M40,100 Q80,60 120,80" fill="none" stroke="#00c8ff" strokeWidth="2" strokeOpacity="0.5" strokeDasharray="4 2" data-id="element-357" />
            </svg>
          </div>

          {/* Vehicle Card */}
          <div className="p-4" data-id="element-358">
            <div className="bg-brand-bg rounded-xl p-4 border border-cyan-subtle" data-id="element-359">
              <div className="flex items-center justify-between mb-3" data-id="element-360">
                <div className="flex items-center gap-2" data-id="element-361">
                  <div className="w-2.5 h-2.5 rounded-full bg-brand-accent" data-id="element-362" />
                  <span className="font-subheading text-sm text-white" data-id="element-363">
                    Honda Civic
                  </span>
                </div>
                <span className="font-mono text-xs text-brand-accent" data-id="element-364">
                  En movimiento
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-365">
                <div data-id="element-366">
                  <p className="font-body text-xs text-brand-text-secondary" data-id="element-367">
                    Velocidad
                  </p>
                  <p className="font-mono text-lg text-brand-accent" data-id="element-368">45 km/h</p>
                </div>
                <div data-id="element-369">
                  <p className="font-body text-xs text-brand-text-secondary" data-id="element-370">
                    Hoy
                  </p>
                  <p className="font-mono text-lg text-white" data-id="element-371">23.4 km</p>
                </div>
              </div>
              <button className="w-full bg-brand-accent text-brand-bg font-subheading font-semibold py-2.5 rounded-lg text-sm" data-id="element-372">
                Bloquear Motor
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function DashcamMockup() {
  return <div className="glass rounded-2xl p-4 shadow-2xl shadow-brand-accent/10" data-id="element-373">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-374">
        <span className="font-subheading font-medium text-white" data-id="element-375">
          Sistema Dashcam
        </span>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/20 border border-red-500/30" data-id="element-376">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-blink" data-id="element-377" />
          <span className="font-mono text-xs text-red-400" data-id="element-378">GRABANDO</span>
        </div>
      </div>

      {/* Camera Feeds */}
      <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-379">
        {/* Front Camera */}
        <div className="bg-brand-bg rounded-lg overflow-hidden" data-id="element-380">
          <div className="flex items-center justify-between px-2 py-1.5 bg-brand-surface" data-id="element-381">
            <span className="font-mono text-[10px] text-brand-text-secondary" data-id="element-382">
              FRONTAL HD
            </span>
            <span className="font-mono text-[10px] text-brand-accent" data-id="element-383">
              1080p
            </span>
          </div>
          <div className="aspect-video bg-brand-surface/50 relative" data-id="element-384">
            {/* Road simulation */}
            <div className="absolute inset-0 flex items-end justify-center pb-4" data-id="element-385">
              <div className="w-20 h-1 bg-brand-accent/30 rounded" data-id="element-386" />
            </div>
            <div className="absolute bottom-2 left-2 font-mono text-[8px] text-brand-accent/60" data-id="element-387">
              19.4326°N -99.1332°W
            </div>
            <div className="absolute bottom-2 right-2 font-mono text-[8px] text-white/60" data-id="element-388">
              45 km/h
            </div>
          </div>
        </div>

        {/* DMS Camera */}
        <div className="bg-brand-bg rounded-lg overflow-hidden" data-id="element-389">
          <div className="flex items-center justify-between px-2 py-1.5 bg-brand-surface" data-id="element-390">
            <span className="font-mono text-[10px] text-brand-text-secondary" data-id="element-391">
              DMS INTERIOR
            </span>
            <span className="font-mono text-[10px] text-brand-accent" data-id="element-392">
              ACTIVO
            </span>
          </div>
          <div className="aspect-video bg-brand-surface/50 relative" data-id="element-393">
            {/* Driver silhouette */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-394">
              <div className="w-10 h-12 border-2 border-brand-accent/30 rounded-full" data-id="element-395" />
            </div>
            <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-brand-accent/20 rounded text-[8px] text-brand-accent" data-id="element-396">
              ATENTO ✓
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Panel */}
      <div className="bg-brand-bg rounded-lg p-3" data-id="element-397">
        <div className="flex items-center justify-between mb-3" data-id="element-398">
          <span className="font-subheading text-sm text-white" data-id="element-399">
            Alertas ADAS
          </span>
          <span className="font-mono text-xs text-brand-text-secondary" data-id="element-400">
            Últimas 24h
          </span>
        </div>
        <div className="space-y-2" data-id="element-401">
          <div className="flex items-center gap-3 p-2 bg-brand-surface/50 rounded" data-id="element-402">
            <div className="w-8 h-8 rounded-lg bg-brand-accent/10 flex items-center justify-center" data-id="element-403">
              <AlertTriangleIcon className="w-4 h-4 text-brand-accent" data-id="element-404" />
            </div>
            <div className="flex-1" data-id="element-405">
              <p className="font-body text-xs text-white" data-id="element-406">
                Cambio de carril sin señal
              </p>
              <p className="font-mono text-[10px] text-brand-text-secondary" data-id="element-407">
                Hoy 14:32
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 bg-brand-surface/50 rounded" data-id="element-408">
            <div className="w-8 h-8 rounded-lg bg-brand-accent/10 flex items-center justify-center" data-id="element-409">
              <AlertTriangleIcon className="w-4 h-4 text-brand-accent" data-id="element-410" />
            </div>
            <div className="flex-1" data-id="element-411">
              <p className="font-body text-xs text-white" data-id="element-412">
                Distancia frontal reducida
              </p>
              <p className="font-mono text-[10px] text-brand-text-secondary" data-id="element-413">
                Hoy 11:15
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}